import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api-client";

/**
 * Forgot password: e-mail → 6-digit code → new password, on one page. Outside the `_app` layout,
 * like the invite page: the person has no session.
 */
export const Route = createFileRoute("/forgot-password")({
  validateSearch: z.object({ email: z.string().optional() }),
  component: ForgotPassword,
});

const emailOk = (v: string) => z.email().safeParse(v.trim()).success;

function ForgotPassword() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [email, setEmail] = useState(search.email ?? "");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready =
    /^\d{6}$/.test(code) &&
    password.length >= 10 &&
    confirm === password &&
    !busy;

  const failed = (e: unknown, fallback: string) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/auth/password/forgot", {
        method: "POST",
        json: { email: email.trim() },
        anonymous: true,
      });
      setSent(true);
    } catch (e) {
      failed(e, "Could not send the code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/auth/password/reset", {
        method: "POST",
        json: { email: email.trim(), code, password },
        anonymous: true,
      });
      toast.success("Password changed. Sign in with the new password.");
      void navigate({ to: "/login" });
    } catch (e) {
      failed(e, "Could not change the password. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md items-center px-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Reset your password</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {!sent ? (
            <>
              <p className="text-sm text-ink-muted">
                Enter your work e-mail and we will send you a 6-digit code.
              </p>
              <Field label="E-mail" htmlFor="fp-email">
                <Input
                  id="fp-email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && emailOk(email) && !busy)
                      void sendCode();
                  }}
                />
              </Field>
              {error && <p className="text-sm text-danger-text">{error}</p>}
              <Button
                className="w-full"
                disabled={!emailOk(email)}
                loading={busy}
                onClick={() => void sendCode()}
              >
                {busy ? "Sending…" : "Send code"}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-ink-muted">
                If <span className="font-medium text-ink">{email.trim()}</span>{" "}
                has a NexSeed account, a code is on its way. It expires in 10
                minutes.
              </p>
              <Field label="Code" htmlFor="fp-code">
                <Input
                  id="fp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              </Field>
              <Field
                label="New password"
                htmlFor="fp-password"
                error={tooShort ? "At least 10 characters." : undefined}
              >
                <Input
                  id="fp-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field
                label="Confirm password"
                htmlFor="fp-confirm"
                error={mismatch ? "The two do not match." : undefined}
              >
                <Input
                  id="fp-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && ready) void reset();
                  }}
                />
              </Field>
              {error && <p className="text-sm text-danger-text">{error}</p>}
              <Button
                className="w-full"
                disabled={!ready && !busy}
                loading={busy}
                onClick={() => void reset()}
              >
                {busy ? "Saving…" : "Set password"}
              </Button>
              <Button
                variant="link"
                className="w-full"
                disabled={busy}
                onClick={() => {
                  setSent(false);
                  setCode("");
                  setError(null);
                }}
              >
                Didn't get it? Send a new code
              </Button>
            </>
          )}
          <p className="text-center text-sm">
            <Link to="/login" className="text-primary-text hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
